// src/redux/floorThunks.ts
import {
  createAsyncThunk,
  ThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit'
import {
  addStoreys,
  addRoof,
  setConvertData,
  setFileName,
} from '../actions/construction'
import { ConvertData } from '../models/Construction'
import {
  AWSFile,
  DrawingFile,
  DrawingType,
  AnalyzeResult,
  LoadingPage,
  FileTag,
  ConvertFailedErrorType,
} from '../models/File'
import {
  addFileDescription,
  setIsLoading,
  sortPages,
  updateDrawingFile,
  updateS3FolderName,
  selectFile,
  setConvertIssues,
  clearConvertResults,
  setConvertFailedError,
} from '../actions/files'
import { RootState } from '../store'
import { getIDToken } from '../utils/AuthUtil'
import { getFileNameWithoutExtension, isDevelop } from '../utils/Common'
import {
  addClient,
  addFile,
  addFiles,
  addProject,
  addUserRecentFiles,
  removeFile,
  updateFile,
  updateSelectedClient,
  updateSelectedFile,
  updateSelectedProject,
} from '../actions/organization'
import { BASE_URL } from '../config'
import { setPermitFile } from '../actions/permit'
import * as pdfjsLib from 'pdfjs-dist'
import { v4 as uuidv4 } from 'uuid'
import { retryFetchAction } from '../utils/ServiceUtil'
import { pdfPagesNumber, pdfToImage } from '../utils/PdfUtil'
import { fetchPaymentStatusAsync } from './paymentService'
import { setHistory } from '../actions/history'
import { fetchBuildingInfo } from './buildingService'

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`

export const fetchFiles = createAsyncThunk(
  'file/fetchFiles',
  async (payload: { projectId?: string }, { dispatch }) => {
    fetch(`${BASE_URL}/api/file/findAll`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filterBy: [
          {
            name: 'project_id',
            value: payload.projectId ? payload.projectId : null,
          },
        ],
      }),
    })
      .then((response) => {
        if (!response.ok)
          return response.json().then((response) => {
            throw new Error(response.error)
          })
        return response.json()
      })
      .then((data) => {
        dispatch(addFiles(data))
      })
      .catch((error) => {
        dispatch(addFiles([]))
        console.error('Error during fetching files: ', error)
      })
  },
)

export const fetchUserRecentFiles = createAsyncThunk(
  'file/fetchUserRecentFiles',
  async (payload: { userId?: number }, { dispatch }) => {
    fetch(`${BASE_URL}/api/recentfiles/findAll`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filterBy: [
          {
            name: 'user_id',
            value: payload.userId ? payload.userId : null,
          },
        ],
        include: {
          file: true,
        },
        orderBy: [
          {
            updatedAt: 'desc',
          },
        ],
      }),
    })
      .then((response) => {
        if (!response.ok)
          return response.json().then((response) => {
            throw new Error(response.error)
          })
        return response.json()
      })
      .then((data) => {
        dispatch(addUserRecentFiles(data))
      })
      .catch((error) => {
        dispatch(addUserRecentFiles([]))
        console.error('Error during fetching files: ', error)
      })
  },
)

export const createFile = createAsyncThunk(
  'file/createFile',
  async (
    payload: {
      filename: string
      projectId?: string
      clientName?: string
      projectName?: string
      organizationId?: string
      clientId?: string
    },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    let file = {
      name: payload.filename,
      status: 'Active',
      project_id: payload.projectId,
      client_id: payload.clientId,
      clientName: payload.clientName,
      projectName: payload.projectName,
      organization_id: payload.organizationId,
    }

    await fetch(`${BASE_URL}/api/file/create`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(file),
    })
      .then((response) => {
        if (!response.ok)
          return response.json().then((response) => {
            throw new Error(response.error)
          })
        return response.json()
      })
      .then((data) => {
        dispatch(addFile(data))
        dispatch(updateSelectedFile(data))

        if (!!data.project) {
          const existingProject = state.organization.projects.find(
            (project) => project.id === data.project.id,
          )
          if (!existingProject) {
            dispatch(addProject(data.project))
            dispatch(updateSelectedProject(data.project))
          }
          if (!!data.project.client) {
            const existingClient = state.organization.clients.find(
              (client) => client.id === data.project.client.id,
            )
            if (!existingClient) {
              dispatch(addClient(data.project.client))
              dispatch(updateSelectedClient(data.project.client))
            }
          }
        }
      })
      .catch((error) => console.error('Error during creating file: ', error))
  },
)

export const updateFileAsync = createAsyncThunk(
  'file/updateFile',
  async (
    payload: { fileId: number; filePath: string },
    { dispatch, getState },
  ) => {
    if (isDevelop()) {
      return
    }

    const file = {
      id: payload.fileId,
      data: { path: payload.filePath },
    }

    fetch(`${BASE_URL}/api/file/update`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(file),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(updateFile({ fileId: payload.fileId, newFile: data }))
        dispatch(fetchPaymentStatusAsync({ fileId: payload.fileId }))
        const state = getState() as RootState
        const selectedFile = state.organization.selectedFile
        if (selectedFile?.id === payload.fileId) {
          dispatch(updateSelectedFile(data))
        }
      })
      .catch((error) => console.error('Error during updating file: ', error))
  },
)

export const updateFileName = createAsyncThunk(
  'file/updateFileName',
  async (payload: { fileId: number; fileName: string }, { dispatch }) => {
    if (isDevelop()) {
      return
    }

    const file = {
      id: payload.fileId,
      data: { name: payload.fileName },
    }

    fetch(`${BASE_URL}/api/file/update`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(file),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(updateFile({ fileId: payload.fileId, newFile: data }))
      })
      .catch((error) => console.error('Error during updating file: ', error))
  },
)

const getSignedUrl = createAsyncThunk(
  'files/getSignedUrlAsync',
  async (payload: { filename: string; foldername?: string }) => {
    const response = await fetch(`${BASE_URL}/api/files/signedUrl`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: payload.filename,
        s3_folder: payload.foldername ? payload.foldername : null,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to get signed URL')
    }

    const data = await response.json()

    return data
  },
)

export const uploadFilesAsync = createAsyncThunk(
  'files/uploadFilesAsync',
  async (
    payload: { files: { id: string; source: File }[] },
    { dispatch, getState },
  ) => {
    const file = payload.files[0].source
    let signedUrl: AWSFile
    try {
      const state = getState() as RootState
      const s3Foldername = state.drawings.selecetedS3Folder
      signedUrl = (await dispatch(
        getSignedUrl({ filename: file.name, foldername: s3Foldername }),
      ).unwrap()) as AWSFile
      dispatch(updateS3FolderName(signedUrl.s3_folder))
    } catch (error) {
      console.error(error)
      return
    }

    fetch(signedUrl.s3_filename, {
      method: 'PUT',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': file.type,
      },
      body: file,
    })
      .then((_) => {
        const state = getState() as RootState
        const existingFile = state.drawings.files.find(
          (file) => file.id === payload.files[0].id,
        )
        const url = new URL(signedUrl.s3_filename)
        dispatch(
          updateDrawingFile({
            fileId: payload.files[0].id,
            newFile: {
              ...existingFile!,
              s3Bucket: signedUrl.s3_bucketname,
              s3Folder: signedUrl.s3_folder,
              s3Filename: decodeURIComponent(url.pathname.replace(/^\/+/, '')),
            },
          }),
        )
      })
      .catch((error) => console.error('Error during uploading files: ', error))
  },
)

export const analyzeFilesAsync = createAsyncThunk(
  'files/analyzeFilesAsync',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const bucket = state.drawings.files[0].s3Bucket
    const files = state.drawings.files.map((file) => file.s3Filename)

    // const queryString = `bucket=${bucket}&files=${files}`
    const url = `${BASE_URL}/api/files/analyze`
    const requestBody = {
      bucket,
      files,
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        }
      })
      .then((data) => {
        dispatch(
          addFileDescription({
            fileId: state.drawings.files[0].id,
            description: data,
          }),
        )
        dispatch(sortPages())
        dispatch(selectFile({ fileName: state.drawings.files[0].name }))
        dispatch(setIsLoading(false))
      })
      .catch((error) => {
        console.error('Error during files analysis: ', error)
      })
  },
)

export const convertTo3DAsync = createAsyncThunk(
  'floors/convertFilesTo3D',
  async (_, { dispatch, getState }) => {
    let state = getState() as RootState
    const analyzeResult: AnalyzeResult = {
      number_of_pages: state.drawings.files.length
        ? state.drawings.files[0].pages.length
        : 0,
      floorplans: [],
      elevations: [],
    }
    for (const page of state.drawings.recognizedPages) {
      if (page.isChecked) {
        if (page.drawingType === DrawingType.Floorplan) {
          analyzeResult.floorplans.push({
            page_number: page.number,
            name: page.name!,
            file_name: page.filename!,
          })
        }
        if (page.drawingType === DrawingType.Elevation) {
          analyzeResult.elevations.push({
            page_number: page.number,
            name: page.name!,
            file_name: page.filename!,
          })
        }
      }
    }

    const url = isDevelop() ? 'building.json' : `${BASE_URL}/api/convert/3D`
    const requestBody = isDevelop()
      ? {}
      : {
          file: {
            s3_bucketname: state.drawings.files[0].s3Bucket,
            s3_folder: state.drawings.files[0].s3Folder,
            s3_file: state.drawings.files[0].s3Filename,
          },
          json: analyzeResult,
          overrides: state.drawings.convertResult?.fixes,
        }

    const request = isDevelop()
      ? {}
      : {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${await getIDToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }

    dispatch(clearConvertResults())

    let convertFailed = false
    const abortController = new AbortController()
    try {
      fetch(url, request)
        .then((response) => {
          if (!response.ok) {
            if (response.status === 404) {
              return response.json()
            }
            if (response.status === 500) {
              dispatch(
                setConvertFailedError(ConvertFailedErrorType.SystemError),
              )
              convertFailed = true
              abortController.abort()
            }
          }
        })
        .then((data) => {
          if (data) {
            convertFailed = true
            abortController.abort()
            dispatch(setIsLoading(false))
            dispatch(setConvertIssues(data))
          }
        })
    } catch (error) {}

    const fileName = state.drawings.files[0].s3Filename
    const filePath = fileName
      ? `${getFileNameWithoutExtension(fileName)}-3dview.json`
      : ''
    dispatch(setFileName(filePath))
    await retryFetchAction(
      dispatch,
      fetchBuildingInfo,
      { fileKey: filePath, withRetry: true },
      30,
      10000,
      abortController,
    ).catch((error) => {
      console.error(error)
      dispatch(setIsLoading(false))
    })

    dispatch(setIsLoading(false))
    state = getState() as RootState
    if (
      !convertFailed &&
      state.construction.convertData.building.storeys.length
    ) {
      const selectedFile = state.organization.selectedFile
      if (!selectFile) {
        console.error('Error: file is not selected')
        return
      }

      dispatch(
        updateFileAsync({
          fileId: selectedFile!.id,
          filePath: filePath,
        }),
      )
    }

    /*if (!response.ok) {
      if (response.status === 504) {
      } else {
        const errorResponse = await response.json()
        console.error(errorResponse.error)
      }

      dispatch(setIsLoading(false))
      return
    }

    const data: Construction = await response.json()
    dispatch(setIsLoading(false))

    const building = data.building
    const filePath = data.json_s3_key
    if (building.storeys.length) {
      for (let i = 0; i < building.storeys.length; i++) {
        const storey = building.storeys[i]
        storey.image = state.drawings.recognizedPages[i]
          ? state.drawings.recognizedPages[i].image
          : ''
      }
      const selectedFile = state.organization.selectedFile
      dispatch(
        updateFileAsync({
          fileId: selectedFile?.id || '',
          filePath: filePath,
        }),
      )
      dispatch(addStoreys({ storeys: building.storeys }))
    }

    if (building.roofs.length) {
      for (let i = 0; i < building.roofs.length; i++) {
        const roof = building.roofs[i]
        dispatch(addRoof({ roof }))
      }
    }*/
  },
)

export const convertIfcAsync = createAsyncThunk(
  'floors/convertIfcAsync',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const url = `${BASE_URL}/api/convert/ifc`
    const requestBody = {
      s3_bucketname: state.drawings.files[0].s3Bucket,
      s3_folder: state.drawings.files[0].s3Folder,
      s3_file: state.drawings.files[0].s3Filename,
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((data: ConvertData) => {
        dispatch(setIsLoading(false))
        const building = data.building
        const filePath = data.json_s3_key
        if (building.storeys.length) {
          for (let i = 0; i < building.storeys.length; i++) {
            const storey = building.storeys[i]
            storey.image = state.drawings.recognizedPages[i]
              ? state.drawings.recognizedPages[i].image
              : ''
          }
          const selectedFile = state.organization.selectedFile
          if (!selectFile) {
            console.error('Error: file is not selected')
            return
          }

          dispatch(
            updateFileAsync({
              fileId: selectedFile!.id,
              filePath: filePath,
            }),
          )
          dispatch(addStoreys({ storeys: building.storeys }))
        }

        if (building.roofs.length) {
          for (let i = 0; i < building.roofs.length; i++) {
            const roof = building.roofs[i]
            dispatch(addRoof({ roof }))
          }
        }
      })
      .catch((error) =>
        console.error('Error during converting IFC to 3D: ', error),
      )
  },
)

export const convertDxfAsync = createAsyncThunk(
  'floors/convertDxfAsync',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const url = isDevelop() ? 'building.json' : `${BASE_URL}/api/convert/dxf`
    const requestBody = isDevelop()
      ? {}
      : {
          file: {
            s3_bucketname: state.drawings.files[0].s3Bucket,
            s3_folder: state.drawings.files[0].s3Folder,
            s3_file: state.drawings.files[0].s3Filename,
          },
          dxf: state.drawings.files.map((drawing) => ({
            // TODO: Temp solution
            type: drawing.tag === FileTag.Roof ? 'ROOF' : 'FLOOR',
            file_name: drawing.name,
            tag: drawing.tag,
          })),
        }
    const request = isDevelop()
      ? {}
      : {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${await getIDToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }

    const response = await fetch(url, request)

    if (!response.ok) {
      if (response.status === 504) {
        const fileName = state.drawings.files[0].s3Filename
        const filePath = fileName
          ? `${getFileNameWithoutExtension(fileName)}-3dview.json`
          : ''

        await retryFetchAction(
          dispatch,
          fetchBuildingInfo,
          { fileKey: filePath, withRetry: true },
          5,
          30000,
        )

        const selectedFile = state.organization.selectedFile
        if (!selectFile) {
          console.error('Error: file is not selected')
          return
        }

        dispatch(
          updateFileAsync({
            fileId: selectedFile!.id,
            filePath: filePath,
          }),
        )
      } else {
        const errorResponse = await response.json()
        console.error(errorResponse.error)
      }

      dispatch(setIsLoading(false))
      return
    }

    const data: ConvertData = await response.json()
    dispatch(setIsLoading(false))

    const building = data.building
    const filePath = data.json_s3_key
    if (building.storeys.length) {
      for (let i = 0; i < building.storeys.length; i++) {
        const storey = building.storeys[i]
        storey.image = state.drawings.recognizedPages[i]
          ? state.drawings.recognizedPages[i].image
          : ''
      }
      const selectedFile = state.organization.selectedFile
      if (!selectFile) {
        console.error('Error: file is not selected')
        return
      }

      dispatch(
        updateFileAsync({
          fileId: selectedFile!.id,
          filePath: filePath,
        }),
      )
      dispatch(addStoreys({ storeys: building.storeys }))
    }

    if (building.roofs.length) {
      for (let i = 0; i < building.roofs.length; i++) {
        const roof = building.roofs[i]
        dispatch(addRoof({ roof }))
      }
    }
  },
)

export const fetchBuildingJson = createAsyncThunk(
  'floors/buildingJson',
  async (
    payload: { fileKey: string; withRetry?: boolean },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const requestBody = { fileKey: payload.fileKey }

    try {
      const response = await fetch(`${BASE_URL}/api/files/download`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        throw new Error(`Server Error: ${response.status}`)
      }

      const convertData: ConvertData = await response.json()
      if (
        !!convertData.building &&
        !!convertData.building.storeys &&
        !!convertData.building.storeys.length &&
        !!convertData.building.storeys.some((storey) => storey.walls)
      ) {
        for (let i = 0; i < convertData.building.storeys.length; i++) {
          const storey = convertData.building.storeys[i]
          storey.image = state.drawings.recognizedPages[i]
            ? state.drawings.recognizedPages[i].image
            : storey.image || ''
        }

        //dispatch(setIsLoading(false))
        dispatch(setConvertData(convertData))
        dispatch(setHistory({ convertData }))
      } else {
        throw new Error('Response is empty')
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error during converting to 3D: ', error)
      }

      if (payload.withRetry) {
        throw error
      }
    }
  },
)

export const fetchPermitFileAsync = createAsyncThunk(
  'file/fetchPermitFile',
  async (_, { dispatch }) => {
    fetch('/3886_ROSS_ROAD_230601 _combined.pdf').then(async (response) => {
      const blob = await response.blob()
      const file = new File([blob], 'example.pdf', { type: 'application/pdf' })
      await loadPermitFile(file, dispatch)
    })
  },
)

export const updateRecentFileAsync = createAsyncThunk(
  'file/updateRecentFile',
  async (payload: { fileId: number; userId: number }, { dispatch }) => {
    const filters = [
      { name: 'file_id', value: payload.fileId },
      { name: 'user_id', value: payload.userId },
    ]

    fetch(`${BASE_URL}/api/recentfiles/createorupdate`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filterBy: filters }),
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch(fetchUserRecentFiles({ userId: data.user_id }))
      })
      .catch((error) => console.error('Error during updating file: ', error))
  },
)

export const deleteFileAsync = createAsyncThunk(
  'file/deleteFileAsync',
  async (payload: { fileId: number }, { dispatch, getState }) => {
    const state = getState() as RootState
    const data = { id: payload.fileId }

    await fetch(`${BASE_URL}/api/file/delete`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${await getIDToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => {
        // dispatch(
        //   fetchFiles({ projectId: state.organization?.selectedProject?.id }),
        // )
        dispatch(removeFile(data))
        dispatch(updateSelectedFile(undefined))
        dispatch(fetchUserRecentFiles({ userId: state.auth.user?.id }))
      })
      .catch((error) => console.error('Error during updating file: ', error))
  },
)

// Temporary logic for loading permit file
// TODO: Remove after fetching real permit file
const loadPermitFile = (
  file: File,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
) => {
  return new Promise<DrawingFile>(async (resolve, reject) => {
    const fileReader = new FileReader()
    const fileId = uuidv4()
    const pages: LoadingPage[] = []

    fileReader.onload = async () => {
      try {
        const pdfData = fileReader.result as string
        const numPages = await pdfPagesNumber(pdfData)
        for (let i = 1; i < numPages + 1; i++) {
          const pageId = uuidv4()
          pages.push({
            id: pageId,
            number: i,
            image: '',
            isLoading: true,
            filename: file.name,
          })
        }

        dispatch(
          setPermitFile({
            permitFile: {
              id: fileId,
              name: file.name,
              pages,
            },
          }),
        )

        pdfToImage(fileId, pages, pdfData, dispatch)
      } catch (error) {
        console.error(error)
      }
    }

    fileReader.onerror = (error) => {
      reject(error)
    }

    fileReader.readAsArrayBuffer(file)
  })
}
